import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Trash2, Sparkles, Filter } from "lucide-react";
import { useAuth } from "../../lib/auth";

export default function FactorLibraries() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState("");
  const [sourceF, setSourceF] = useState("");
  const [categoryF, setCategoryF] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    name:"", description:"", source:`Custom-${user?.tenant_id || "tenant"}`,
    region:"", category:"electricity", unit_from:"kWh", unit_to:"kgCO2e",
    factor_value:0, valid_from:new Date().toISOString().slice(0,10), valid_to:null, kpi_id:""
  });

  const load = async () => {
    const params = {};
    if (asOf) params.as_of = asOf;
    if (sourceF) params.source = sourceF;
    if (categoryF) params.category = categoryF;
    const r = await api.get("/admin/factors", { params });
    setData(r.data);
  };
  useEffect(() => { load(); }, [asOf, sourceF, categoryF]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/factors", { ...form, kpi_id: form.kpi_id || null });
      setShow(false);
      await load();
    } catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this factor? Historical computations referencing it will need re-validation.")) return;
    await api.delete(`/admin/factors/${id}`);
    await load();
  };

  const aiSync = async () => {
    try {
      const r = await api.post("/admin/factors/ai-sync");
      alert("AI-synced 1 factor: " + r.data.new_factor.name);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "AI sync requires platform admin");
    }
  };

  if (!data) return <div className="p-6 text-ink-soft">Loading…</div>;
  const sources = Array.from(new Set(data.factors.map(f => f.source)));
  const categories = Array.from(new Set(data.factors.map(f => f.category)));
  const isPlatformAdmin = user?.role === "platform_admin";

  return (
    <div className="p-6 space-y-5" data-testid="factors-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label">Admin · Factor Libraries</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Emission Factor Libraries</h1>
          <p className="text-sm text-ink-soft max-w-2xl">
            Date-versioned emission factors syndicated from public sources (DEFRA, IEA, EPA, IPCC) plus your custom factors.
            Historical activity data is computed using the factor that was valid at that period — historical integrity is preserved.
          </p>
        </div>
        <div className="flex gap-2">
          {isPlatformAdmin && (
            <button data-testid="factor-aisync-button" onClick={aiSync} className="btn btn-secondary"><Sparkles size={14}/> AI sync</button>
          )}
          <button data-testid="factor-new-button" onClick={()=>setShow(true)} className="btn btn-primary"><Plus size={14}/> Custom factor</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3 flex flex-wrap items-center gap-2" data-testid="factor-filters">
        <Filter size={14} className="text-ink-soft"/>
        <span className="dlabel">As of</span>
        <input data-testid="factor-asof" type="date" value={asOf} onChange={e=>setAsOf(e.target.value)} className="border border-border rounded-md px-2 py-1 text-xs"/>
        <span className="dlabel ml-3">Source</span>
        <select data-testid="factor-source" value={sourceF} onChange={e=>setSourceF(e.target.value)} className="border border-border rounded-md px-2 py-1 text-xs">
          <option value="">all</option>{sources.map(s => <option key={s}>{s}</option>)}
        </select>
        <span className="dlabel ml-3">Category</span>
        <select data-testid="factor-category" value={categoryF} onChange={e=>setCategoryF(e.target.value)} className="border border-border rounded-md px-2 py-1 text-xs">
          <option value="">all</option>{categories.map(c => <option key={c}>{c}</option>)}
        </select>
        <span className="ml-auto text-xs text-ink-soft">{data.count} factors visible{asOf && ` valid on ${asOf}`}</span>
      </div>

      <div className="card overflow-hidden">
        <table className="dense">
          <thead>
            <tr>
              <th>Name</th><th>Source</th><th>Region</th><th>Category</th>
              <th>From → To</th><th className="text-right">Factor</th>
              <th>Valid from</th><th>Valid to</th><th>Scope</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data.factors.map(f => (
              <tr key={f.id} data-testid={`factor-${f.id}`}>
                <td className="font-medium">{f.name}{f.ai_synced && <span className="ml-1 pill pill-info text-[10px]">✦ AI</span>}</td>
                <td>{f.source}</td>
                <td className="text-xs">{f.region}</td>
                <td><span className="pill">{f.category}</span></td>
                <td className="font-mono text-xs">{f.unit_from} → {f.unit_to}</td>
                <td className="num font-semibold">{f.factor_value}</td>
                <td className="font-mono text-xs">{f.valid_from}</td>
                <td className="font-mono text-xs">{f.valid_to || <span className="text-status-success">current</span>}</td>
                <td>{f.tenant_id ? <span className="pill pill-warn">custom</span> : <span className="pill pill-info">syndicated</span>}</td>
                <td className="text-right">
                  {(f.tenant_id === user?.tenant_id || isPlatformAdmin) && (
                    <button data-testid={`factor-del-${f.id}`} onClick={()=>remove(f.id)} className="btn btn-ghost text-status-error"><Trash2 size={12}/></button>
                  )}
                </td>
              </tr>
            ))}
            {data.factors.length === 0 && (
              <tr><td colSpan={10} className="text-center py-6 text-ink-soft">No factors match the filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={()=>setShow(false)}>
          <div className="card w-full max-w-lg p-5 bg-white" onClick={e=>e.stopPropagation()}>
            <div className="section-label">New custom factor</div>
            <form onSubmit={create} className="mt-3 space-y-3 text-sm">
              <div>
                <label className="dlabel">Name</label>
                <input data-testid="factor-form-name" required value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="dlabel">Source</label><input data-testid="factor-form-source" value={form.source} onChange={e=>setForm({...form, source:e.target.value})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm"/></div>
                <div><label className="dlabel">Region</label><input data-testid="factor-form-region" value={form.region} onChange={e=>setForm({...form, region:e.target.value})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm"/></div>
                <div><label className="dlabel">Category</label>
                  <select data-testid="factor-form-category" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm">
                    {["electricity","fuel","transport","refrigerant","water","waste","scope3"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="dlabel">Unit from</label><input data-testid="factor-form-uf" required value={form.unit_from} onChange={e=>setForm({...form, unit_from:e.target.value})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm"/></div>
                <div><label className="dlabel">Unit to</label><input data-testid="factor-form-ut" required value={form.unit_to} onChange={e=>setForm({...form, unit_to:e.target.value})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm"/></div>
                <div><label className="dlabel">Factor value</label><input data-testid="factor-form-fv" required type="number" step="any" value={form.factor_value} onChange={e=>setForm({...form, factor_value:Number(e.target.value)})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm"/></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="dlabel">Valid from</label><input data-testid="factor-form-vf" required type="date" value={form.valid_from} onChange={e=>setForm({...form, valid_from:e.target.value})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm"/></div>
                <div><label className="dlabel">Valid to (optional)</label><input data-testid="factor-form-vt" type="date" value={form.valid_to||""} onChange={e=>setForm({...form, valid_to:e.target.value || null})} className="mt-1 w-full border border-border rounded-md px-2 py-1.5 text-sm"/></div>
              </div>
              <div>
                <label className="dlabel">Description</label>
                <textarea data-testid="factor-form-desc" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm h-16"/>
              </div>
              <div className="text-[11px] text-ink-soft">
                Adding a factor with the same name+region+category automatically supersedes the previous version (valid_to set to {form.valid_from}).
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={()=>setShow(false)} className="btn btn-ghost">Cancel</button>
                <button data-testid="factor-form-submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
